import { Platform, StyleSheet, type ViewStyle } from 'react-native';
import { colors } from './colors';
import { tokens } from './tokens';

/**
 * The depth budget. Every container in the app sits at one of three levels,
 * and only level 1 is allowed a border:
 *
 *   Level 0  canvas          background.primary; never bordered
 *   Level 1  card            one hairline border + fill + blur, once per branch
 *   Level 2  rows and wells  a translucent fill; no border, no shadow
 *
 * `cardSurface` and `innerSurface` are the only sanctioned way to build the
 * two container levels, so a card can never quietly grow a second border or a
 * row a competing one.
 */

type SurfacePlatform = 'web' | 'native';

interface CardSurfaceOptions {
  /** Defaults to the running platform; pass explicitly to render the other. */
  platform?: SurfacePlatform;
  hovered?: boolean;
  /** Selected or otherwise important: teal border + halo, never an edge stripe. */
  selected?: boolean;
}

interface InnerSurfaceOptions {
  hovered?: boolean;
}

const SELECTED_HALO = '0 0 32px rgba(0, 196, 180, 0.42)';

const currentPlatform = (): SurfacePlatform => (Platform.OS === 'web' ? 'web' : 'native');

export function cardSurface(options: CardSurfaceOptions = {}): ViewStyle {
  const { platform = currentPlatform(), hovered = false, selected = false } = options;
  const { fill, fillHover, border, sheen, blur } = colors.surface.bento;

  const accented = hovered || selected;
  const base: ViewStyle = {
    borderWidth: 1,
    borderColor: accented ? colors.primary.borderActive : border,
    borderRadius: tokens.radius.xl,
  };

  if (platform !== 'web') {
    // The gradient fill and the glow are painted by BentoSurface and the
    // native shadow tokens; anything web-only would be silently dropped here.
    return base;
  }

  const shadow = selected ? `inset 0 1px 0 ${sheen}, ${SELECTED_HALO}` : `inset 0 1px 0 ${sheen}`;

  return {
    ...base,
    backgroundColor: hovered ? fillHover : fill,
    ...({ backdropFilter: `blur(${blur}px)` } as ViewStyle),
    ...({ boxShadow: shadow } as ViewStyle),
    ...(hovered ? { transform: [{ translateY: -2 }] } : {}),
    ...({
      transitionProperty: 'background-color, border-color, transform',
      transitionDuration: `${tokens.duration.fast}ms`,
      transitionTimingFunction: tokens.easing.default,
    } as ViewStyle),
  };
}

export function innerSurface(options: InnerSurfaceOptions = {}): ViewStyle {
  return {
    backgroundColor: options.hovered ? colors.surface.rowHover : colors.surface.row,
    borderRadius: tokens.radius.smPlus,
  };
}

/**
 * A row in a list inside a card.
 *
 * Distinct from `innerSurface`, which is a well: a single grouped box that
 * should read as its own region. A list row repeats, and a fill that repeats
 * ten times down a card stops reading as grouping and starts reading as ten
 * grey slabs. So a row is bare at rest, separated from its neighbour by
 * `listDivider`, and only takes a fill when the pointer is on it.
 */
export function listRow(options: InnerSurfaceOptions = {}): ViewStyle {
  return {
    backgroundColor: options.hovered ? colors.surface.row : 'transparent',
    borderRadius: tokens.radius.smPlus,
  };
}

/** The hairline between two list rows. */
export const listDivider: ViewStyle = {
  height: StyleSheet.hairlineWidth,
  backgroundColor: colors.border.subtle,
};
